import { useState } from 'react';
import { TechPattern } from '@/components/brand/TechPattern';
import type { SiteLocation } from '@/content/site';

interface LocationMapProps {
  /** readonly: site.locations is declared `as const`. */
  locations: readonly SiteLocation[];
  className?: string;
}

/**
 * Builds the embed URL for a city.
 *
 * City-level only. site.ts records that street addresses are deliberately not
 * published, and a map is not a reason to quietly change that.
 *
 * This uses the keyless `output=embed` endpoint rather than the Maps Embed
 * API, so the site needs no API key and no billing account. That endpoint is
 * undocumented, which is the trade-off: it is isolated in this one function so
 * swapping to `/maps/embed/v1/place?key=...` later is a one-line change.
 */
function embedUrl(loc: SiteLocation): string {
  const query = `${loc.city}, ${loc.region}, ${loc.country}`;
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function mapsLink(loc: SiteLocation): string {
  const query = `${loc.city}, ${loc.region}, ${loc.country}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * The office map for the contact page.
 *
 * The map loads with the page. `loading="lazy"` still defers the request until
 * the frame is near the viewport, so it costs nothing on first paint, but no
 * click is required — a visitor who scrolls to this section sees the map.
 *
 * Note this is the site's only third-party embed: loading the contact page now
 * tells Google about the visit and lets it set cookies in that frame. That is
 * stated plainly in content/privacy.ts, which must stay in step with it.
 *
 * The map is NOT colour-filtered to match the brand. Every filter that pushes
 * a map toward navy also recolours Google's own street and city labels and the
 * attribution, which drops them below the 4.5:1 contrast minimum on content we
 * do not control. The navy framing comes from the chrome around it instead.
 */
export function LocationMap({ locations, className = '' }: LocationMapProps) {
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState(false);

  const loc = locations[active];
  if (!loc) return null;

  const query = `${loc.city}, ${loc.region}, ${loc.country}`;

  return (
    <div className={`overflow-hidden rounded-card border border-hairline shadow-card ${className}`}>
      {/* Navy header strip: this is what makes the module read as branded. */}
      <div className="relative flex flex-wrap items-center justify-between gap-4 overflow-hidden bg-ink px-5 py-4 text-white sm:px-6">
        <TechPattern opacity={0.05} size={110} />

        <div className="relative z-10 flex flex-wrap gap-2">
          {locations.map((l, i) => (
            <button
              key={l.city}
              type="button"
              onClick={() => {
                setActive(i);
                setFailed(false);
              }}
              aria-pressed={i === active}
              className={`min-h-11 rounded-full px-5 text-sm font-medium transition-colors duration-500 ease-brand ${
                i === active
                  ? 'bg-white text-ink'
                  : 'border border-white/20 text-white/70 hover:border-white/50 hover:text-white'
              }`}
            >
              {l.city}
            </button>
          ))}
        </div>

        <p className="text-caption relative z-10 text-white/60">
          {loc.region} · {loc.timezone}
        </p>
      </div>

      {/*
        Fixed aspect so the frame never shifts layout as it loads. 16/10 holds
        at every width — the module sits in a half-width column at lg, where a
        2/1 letterbox was too shallow to show any useful area around the city.
      */}
      <div className="relative aspect-[16/10] w-full bg-surface-muted">
        {failed ? (
          /* Branded fallback, shown only if the embed genuinely fails. */
          <div className="absolute inset-0 grid place-items-center bg-linear-to-br from-ink-3 via-ink to-brand-mark px-6 text-center">
            <TechPattern opacity={0.07} size={120} />
            <div className="relative z-10">
              <h3
                className="font-display text-2xl font-extrabold uppercase text-white"
                style={{ fontStretch: '88%' }}
              >
                {loc.city}, {loc.country}
              </h3>
              <p className="text-caption mx-auto mt-3 max-w-[36ch] text-white/70">
                The map could not be loaded.{' '}
                <a
                  href={mapsLink(loc)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline underline-offset-2 hover:text-brand-accent"
                >
                  Open it in Google Maps
                </a>
                .
              </p>
            </div>
          </div>
        ) : (
          <iframe
            /*
              key forces a fresh element per city. Mutating an iframe's src
              instead pushes a browser history entry, which would make the
              back button step through map states rather than leave the page.
            */
            key={loc.city}
            src={embedUrl(loc)}
            title={`Map of ${query}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            onError={() => setFailed(true)}
            className="absolute inset-0 h-full w-full border-0"
          />
        )}
      </div>
    </div>
  );
}
